import { makeStyles } from 'tss-react/mui';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  ContentTemplate,
  ErrorInfo,
  HeaderTemplate,
  Typography,
} from 'web-library';
import { useGetAuditEligibility } from '../../../api-refactored/user/useGetAuditEligibility';
import { SearchDate } from '../../../models';
import {DateInput, useSearchFunction} from '../logic/useTransactionLog';
import SelectTransactionLog from './SelectTransactionLog';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

const useStyles = makeStyles()((theme) =>
({
  warningText: {
    width: '100%',
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  formElementContainer: {
    marginBottom: theme.spacing(2),
    width: '30%',
    marginRight: '5%',
    display: 'inline-block',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  errorContainer: {
    display: 'flex',
  },
  label: {
    color: theme.palette.text.secondary,
  },
  submitButtonContainer: {
    width: '100%',
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
}));

const TransactionLogList = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [isError, setIsError] = useState(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchTouched, setSearchTouched] = useState(false);
  const [initData, setInitData] = useState<DateInput>({
    startDate: '',
    endDate: '',
  });
  const [isFetchingTableData, setIsFetchingTableData] = useState(false);

  const handleFetchingTableData = () => {
    setIsFetchingTableData(false);
  };

  useGetAuditEligibility();

  const { form } = useSearchFunction();

  const { handleSubmit, control, formState: { errors }, watch, trigger, setValue } = form;

  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');

  useEffect(() => {
    if ((watchStartDate && searchTouched) || (watchEndDate && searchTouched)) {
      trigger('endDate');
      trigger('startDate');
    }
  }, [watchStartDate, trigger, searchTouched, watchEndDate]);

  const checkStartDate = () => {
    if (startDate && endDate) {
      if (dayjs(endDate).diff(startDate, 'day') < 0) {
        form.setError('endDate', {
          type: 'custom',
          message: 'End Date should be greater than Start Date',
        });
        setIsError(true);
        resetInitdata();
        return false;
      }
      if (dayjs(endDate).diff(startDate, 'month', true) > parseFloat('3.0')) {
        form.setError('root.custom', {
          type: 'manual',
          message: 'Search range should not be more than 3 months.',
        });
        setIsError(true);
        resetInitdata();
        return false;
      }
    }
    setIsError(false);
    return true;
  };

  /**
   * To reset the init date range when date range check failed.
   * To avoid children component getting invalid date range.
   */
  const resetInitdata = () => {
    setInitData({
      startDate: '',
      endDate: '',
    });
  };

  const checkDateRangeHandler = (formDate: SearchDate) => {
    // set init data only if date range is valid, to avoid unnecessary search API triggered
    const checkResult = checkStartDate();
    if (checkResult) {
      setInitData({
        startDate: formDate.startDate,
        endDate: formDate.endDate,
      });
      setIsFetchingTableData(true);
    }
  };

  /**
   * On page initialize, set searchTouched to true only if there is no error
   * Once set to true, isError state will be used to track API call errors
   */
  const onSearchClickedHandler = async () => {
    if (Object.keys(errors).length === 0 && errors.constructor === Object) {
      setSearchTouched(true);
    }
  };

  return (
    <Box>
      <HeaderTemplate>
        <form noValidate onSubmit={handleSubmit(checkDateRangeHandler)}>
          <Box className={classes.formElementContainer}>
            <ControlledDatePicker
              label={t('admin->>TransactionLogList.Start Date')}
              onChange={(date) => {
                setStartDate(date || '');
                setValue('startDate', date || '');
              }}
              control={control}
              name="startDate"
              disableFuture
              error={!!errors.startDate}
              helperText={errors?.startDate?.message?.toString()}
              inputVariant="filled"
              autoOk
            />
          </Box>

          <Box className={classes.formElementContainer}>
            <ControlledDatePicker
              label={t('admin->>TransactionLogList.End Date')}
              onChange={(date) => {
                setEndDate(date || '');
                setValue('endDate', date || '');
              }}
              control={control}
              name="endDate"
              maxDate={dayjs()}
              disableFuture
              error={!!errors.endDate}
              helperText={errors?.endDate?.message?.toString()}
              inputVariant="filled"
              autoOk
            />
          </Box>

          <Box className={classes.errorContainer}>
            <Typography className={classes.label} variant="subtitle2">
              {t('admin->>TransactionLogList.Date range should not be more than 3 months')}
            </Typography>
          </Box>
          {errors?.root?.custom?.message !== '' && (
            <Box className={classes.formElementContainer}>
              <ErrorInfo label={errors?.root?.custom?.message} />
            </Box>
          )}

          <Box className={classes.submitButtonContainer}>
            <ApiButton
              status={isFetchingTableData ? 'loading' : 'idle'}
              type="submit"
              onClick={onSearchClickedHandler}
            >
              {t('admin->>TransactionLogList.Search')}
            </ApiButton>
          </Box>
        </form>
      </HeaderTemplate>
      {searchTouched && !isError && (
        <ContentTemplate type="full">
          <SelectTransactionLog
            initData={initData}
            translations={{
              accountType: t('admin->>TransactionLogList.Account Type'),
              userName: t('admin->>TransactionLogList.Name'),
              role: t('admin->>TransactionLogList.Role '),
              action: t('admin->>TransactionLogList.Action'),
              actionDateTime: t('admin->>TransactionLogList.Action Date/Time'),
              applicationId: t('admin->>TransactionLogList.Application ID'),
              status: t('admin->>TransactionLogList.Status'),
            }}
            onHandleFetchingTableData={handleFetchingTableData}
            isFetchingTableData={isFetchingTableData}
          />
        </ContentTemplate>
      )}
    </Box>
  );
};

export default TransactionLogList;
